import { useEffect, useRef, useLayoutEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const SCROLL_STORAGE_KEY = 'fornbo_scroll_positions';

const safeParseScrollPositions = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SCROLL_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

export default function ScrollToTop() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const pathname = location.pathname;
  const hash = location.hash;
  const previousHashRef = useRef(hash);
  const hasRestoredRef = useRef(false);

  // Disable browser's default scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    return () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  // Clear hash from URL when navigating to a non-home page
  useEffect(() => {
    if (pathname !== '/' && hash) {
      window.history.replaceState(null, '', pathname + location.search);
    }
  }, [pathname, hash]);

  // Use useLayoutEffect to run BEFORE browser paints
  useLayoutEffect(() => {
    const isHomePage = pathname === '/';
    const isBackNavigation = navigationType === 'POP';
    const previousHash = previousHashRef.current;

    if (isHomePage) {
      if (isBackNavigation) {
        // Navigated back to home from a sub-page
        if (!hasRestoredRef.current) {
          hasRestoredRef.current = true;
          const scrollPositions = safeParseScrollPositions();
          const savedPosition = scrollPositions['home'];

          if (savedPosition !== undefined && savedPosition >= 0) {
            // Clear hash immediately to prevent browser auto-scroll
            if (window.location.hash) {
              window.history.replaceState(null, '', pathname + location.search);
            }
            // Restore scroll position synchronously before paint
            window.scrollTo({ top: savedPosition, behavior: 'auto' });
          } else if (hash) {
            try {
              const element = document.querySelector(hash);
              if (element) element.scrollIntoView({ behavior: 'auto' });
            } catch {}
          }
        }
      } else if (hash && hash !== previousHash) {
        // Direct hash navigation (not back navigation)
        hasRestoredRef.current = false;
        try {
          const element = document.querySelector(hash);
          if (element) {
            setTimeout(() => element.scrollIntoView({ behavior: 'smooth' }), 100);
          }
        } catch {}
      } else {
        // Normal navigation to home (not back) - reset flag
        hasRestoredRef.current = false;
      }
    } else {
      // We're on a sub-page - scroll to top and reset flag
      hasRestoredRef.current = false;
      window.scrollTo({ top: 0, behavior: 'auto' });
    }

    previousHashRef.current = hash;
  }, [pathname, hash, navigationType, location.search]);

  return null;
}
